/**
 * Shared CGuard TypeScript types — tenancy, auth, telemetry primitives.
 * Generated API types live in `@cguard/api-client`. This package is for
 * hand-curated cross-cutting types only.
 */

export type Brand<T, B> = T & { readonly __brand: B };

export type SspId = Brand<string, 'SspId'>;
export type SsuId = Brand<string, 'SsuId'>;
export type SiteId = Brand<string, 'SiteId'>;
export type CameraId = Brand<string, 'CameraId'>;
export type IncidentId = Brand<string, 'IncidentId'>;

export type TenantTier = 'sira' | 'ssp' | 'ssu';

export interface TenantContext {
  readonly tier: TenantTier;
  readonly sspId?: SspId;
  readonly ssuId?: SsuId;
}

export interface AuthSession {
  readonly subject: string;
  readonly email: string;
  readonly roles: ReadonlyArray<string>;
  readonly tenant: TenantContext;
  readonly accessTokenExp: number;
}

/**
 * Shared domain-enum constants.
 *
 * Backend (services/central/src/Modules/*) declares C# enums and serialises
 * them as PascalCase strings (`HasConversion<string>()` on the EF mapping,
 * default `System.Text.Json` ignores `JsonStringEnumConverter` casing on
 * `Web` defaults). Frontends MUST compare against these constants instead
 * of typing lowercase literals — pre-2026-05-21 the SPAs compared `'active'`
 * which silently never matched the wire format `'Active'`.
 *
 * Each block mirrors a C# enum verbatim:
 *   - `SspStatus`       → `Modules/Tenants/Domain/Ssps/SspStatus.cs`
 *   - `BillingPlan`     → `Modules/Tenants/Domain/Ssps/SspStatus.cs`
 *   - `SsuStatus`       → `Modules/Tenants/Domain/Ssus/SsuStatus.cs`
 *   - `SiteStatus`      → `Modules/Sites/Domain/Sites/SiteEnums.cs`
 *   - `SiteTier`        → `Modules/Sites/Domain/Sites/SiteEnums.cs`
 *   - `BillingTier`     → `Modules/Sites/Domain/Sites/SiteEnums.cs`
 *   - `IncidentStatus`  → `Modules/AlarmsIncidents/Domain/Incidents/IncidentEnums.cs`
 *   - `IncidentTier`    → `Modules/AlarmsIncidents/Domain/Incidents/IncidentEnums.cs`
 */
export const SspStatus = {
  Active: 'Active',
  Suspended: 'Suspended',
  Decommissioned: 'Decommissioned',
  PendingActivation: 'PendingActivation',
} as const;
export type SspStatus = (typeof SspStatus)[keyof typeof SspStatus];

export const BillingPlan = {
  Standard: 'Standard',
  Volume: 'Volume',
  Government: 'Government',
  Enterprise: 'Enterprise',
} as const;
export type BillingPlan = (typeof BillingPlan)[keyof typeof BillingPlan];

export const SsuStatus = {
  Active: 'Active',
  Suspended: 'Suspended',
  Offboarded: 'Offboarded',
} as const;
export type SsuStatus = (typeof SsuStatus)[keyof typeof SsuStatus];

export const SiteStatus = {
  Provisioning: 'Provisioning',
  Live: 'Live',
  Maintenance: 'Maintenance',
  Decommissioned: 'Decommissioned',
} as const;
export type SiteStatus = (typeof SiteStatus)[keyof typeof SiteStatus];

export const SiteTier = {
  Commercial: 'Commercial',
  HighRisk: 'HighRisk',
} as const;
export type SiteTier = (typeof SiteTier)[keyof typeof SiteTier];

export const BillingTier = {
  Tier1: 'Tier1',
  Tier2: 'Tier2',
  Tier3: 'Tier3',
} as const;
export type BillingTier = (typeof BillingTier)[keyof typeof BillingTier];

export const IncidentStatus = {
  Open: 'Open',
  Acknowledged: 'Acknowledged',
  Dispatched: 'Dispatched',
  Closed: 'Closed',
  Transferred: 'Transferred',
} as const;
export type IncidentStatus = (typeof IncidentStatus)[keyof typeof IncidentStatus];

export const IncidentTier = {
  Unknown: 'Unknown',
  Tier1: 'Tier1',
  Tier2: 'Tier2',
  Tier3: 'Tier3',
  Tier4: 'Tier4',
  Tier5: 'Tier5',
} as const;
export type IncidentTier = (typeof IncidentTier)[keyof typeof IncidentTier];

/**
 * Model promotion / cohort enums.
 *
 * Backend `Models` module is not yet implemented in
 * `services/central/src/Modules/` — these constants are FE-canonical for the
 * Day 6 admin-console alignment. When the backend Models module lands, its
 * `ModelTier` / `ModelCohort` C# enums should serialise to the same PascalCase
 * strings (`HasConversion<string>()` convention) so this block stays
 * authoritative on both sides.
 *
 * `ModelCohort` describes a fleet rollout band (used by `OtaBundle.cohort`
 * and `EdgeApplianceResponse.Cohort` already). `ModelTier` is the promotion
 * target a model artefact can occupy: canary → staging → prod.
 */
export const ModelCohort = {
  Canary: 'Canary',
  Early: 'Early',
  Stable: 'Stable',
  Lagging: 'Lagging',
} as const;
export type ModelCohort = (typeof ModelCohort)[keyof typeof ModelCohort];

export const ModelTier = {
  Canary: 'Canary',
  Staging: 'Staging',
  Prod: 'Prod',
} as const;
export type ModelTier = (typeof ModelTier)[keyof typeof ModelTier];

/**
 * Generic pagination envelope returned by every `MapGet("/")` endpoint that
 * wraps a `PagedResult<T>` from `CGuard.Common.Application.Pagination`.
 *
 * The wire shape is camelCase (`PropertyNamingPolicy = JsonNamingPolicy.CamelCase`
 * via `JsonSerializerDefaults.Web`). Hooks that consume a paged endpoint should
 * either keep the envelope (when rendering page controls) or project to
 * `T[]` via React Query's `select` option.
 */
export interface PagedResult<T> {
  readonly items: ReadonlyArray<T>;
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly totalPages?: number;
}

/**
 * Defensive unwrap for paged endpoints. Tolerates both the envelope shape
 * (`{items, page, pageSize, total}`) and the legacy flat-array shape so we
 * don't regress against any endpoint that hasn't been paginated yet.
 */
export function unwrapPaged<T>(input: PagedResult<T> | ReadonlyArray<T> | null | undefined): T[] {
  if (input == null) return [];
  if (Array.isArray(input)) return [...input];
  const envelope = input as PagedResult<T>;
  return envelope.items ? [...envelope.items] : [];
}
